import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "react-toastify";
import SidePanel from "./sidePanel";
import GenerateFirstLetterBox from "./generateFirstLetterBox";
import CommentModal from "./commentModal";
import { CalendarIcon1Svg } from "../svgs/calendarIcon1Svg";
import { ClockIcon1Svg } from "../svgs/clockIcon1Svg";
import { redirectToWhatsapp } from "../utils/redirectToWhatsapp";
import { calculateEndTimeOfAppointment } from "../utils/calculateEndTimeOfAppointment";
import { hebrewDayNameGenerator } from "../utils/hebrewDayNameGenerator";
import CLASS from "../api/services/CLASS";
import APPOINTMENTS from "../api/services/APPOINTMENTS";
import CUSTOMERS from "../api/services/CUSTOMERS";
import { paths } from "../routes/paths";
import styles from "../styles/_components/classDetailsModal.module.scss";

const ClassDetailsModal = ({ appt, setIsOpen, setSuccess, onClientClick }) => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentModalFor, setCommentModalFor] = useState(null);

  // Quick assign state
  const [showAssign, setShowAssign] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(null);

  const businessId = JSON.parse(localStorage.getItem("user_id"));

  const fetchParticipants = useCallback(async () => {
    try {
      const data = await CLASS.get_participants_of_class(appt._id);
      if (Array.isArray(data)) {
        setMembers(data);
      } else if (data?.data) {
        setMembers(data.data);
      }
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  }, [appt._id]);

  useEffect(() => {
    fetchParticipants();
  }, [fetchParticipants]);

  const serviceName = appt.serviceId?.name || "";
  const dateStr = appt.date
    ? format(new Date(appt.date), "dd/MM/yyyy")
    : "";
  const dayName = appt.date
    ? hebrewDayNameGenerator(new Date(appt.date).getDay())
    : "";
  const endTime = calculateEndTimeOfAppointment(
    appt.timeSlot,
    appt.serviceId?.time
  );
  const maxQueue = appt.serviceId?.maximumQueue || 0;
  const count = members.length;

  // Mark attendance — update local state immediately, then sync with server
  const markAttendance = async (hasAttended, userId) => {
    // Optimistic update
    setMembers((prev) =>
      prev.map((m) =>
        m.userId === userId ? { ...m, hasAttended } : m
      )
    );
    try {
      await CLASS.mark_attendance({
        appointmentId: appt._id,
        userId,
        hasAttended,
      });
    } catch (error) {
      toast.error("שגיאה בעדכון נוכחות");
      fetchParticipants(); // revert on error
    }
  };

  // Cancel specific participant
  const handleCancelParticipant = async (userId) => {
    if (!window.confirm("האם לבטל את התור של משתתף/ת זה?")) return;
    try {
      const res = await APPOINTMENTS.cancel_appointment({
        id: appt._id,
        userId,
        status: 3,
      });
      toast.success(res.data.message);
      fetchParticipants();
      setSuccess(true);
    } catch (error) {
      toast.error("שגיאה בביטול");
    }
  };

  // Navigate to full class management
  const goToFullView = () => {
    const employeeId = appt.employeeId?._id || appt.employeeId;
    navigate(paths.class(appt._id, employeeId));
    setIsOpen(false);
  };

  // Search clients for quick assign
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await CUSTOMERS.get(1, 10, "all", searchQuery);
        const rawData = res?.data || res;
        const clients = Array.isArray(rawData) ? rawData : rawData?.data || [];
        // Filter out already-enrolled members
        const memberIds = members.map((m) => m.userId);
        const filtered = clients.filter(
          (c) => !memberIds.includes(c.userId || c._id)
        );
        setSearchResults(filtered.slice(0, 8));
      } catch (err) {
        console.log("Search error:", err);
      }
      setSearchLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, members]);

  // Assign client to this class
  const handleAssignClient = async (client) => {
    const userId = client.userId || client._id;
    const userName = client.name || "הלקוח/ה";
    setAssignLoading(userId);

    const payload = {
      employeeId: businessId,
      userId,
      serviceId: appt.serviceId?._id || appt.serviceId,
      date: appt.date,
      timeSlot: appt.timeSlot,
      cash: true,
    };

    try {
      const res = await APPOINTMENTS.create(payload);
      if (res.data?.code === 200 || res.data?.code === 201) {
        toast.success(`${userName} שובץ/ה לשיעור`);
        setSearchQuery("");
        setSearchResults([]);
        setShowAssign(false);
        fetchParticipants();
        if (setSuccess) setSuccess(true);
      } else {
        const msg = res.data?.message || "שגיאה בשיבוץ";
        const shouldForce = window.confirm(`${msg}\n\nהאם לשבץ בכל זאת?`);
        if (shouldForce) {
          const forceRes = await APPOINTMENTS.create({ ...payload, isForcedBooking: true });
          if (forceRes.data?.code === 200 || forceRes.data?.code === 201) {
            toast.success(`${userName} שובץ/ה לשיעור`);
            setSearchQuery("");
            setSearchResults([]);
            setShowAssign(false);
            fetchParticipants();
            if (setSuccess) setSuccess(true);
          }
        }
      }
    } catch (error) {
      const msg = error.response?.data?.message || "שגיאה בשיבוץ";
      const shouldForce = window.confirm(`${msg}\n\nהאם לשבץ בכל זאת?`);
      if (shouldForce) {
        try {
          await APPOINTMENTS.create({ ...payload, isForcedBooking: true });
          toast.success(`${userName} שובץ/ה לשיעור`);
          setSearchQuery("");
          setShowAssign(false);
          fetchParticipants();
          if (setSuccess) setSuccess(true);
        } catch (e2) {
          toast.error(e2.response?.data?.message || "שגיאה בשיבוץ");
        }
      }
    }
    setAssignLoading(null);
  };

  const getStatusBadge = (hasAttended) => {
    if (hasAttended === true)
      return <span className={styles.badgeSuccess}>הגיע/ה</span>;
    if (hasAttended === false)
      return <span className={styles.badgeError}>לא הגיע/ה</span>;
    return <span className={styles.badgePending}>ממתין</span>;
  };

  return (
    <>
      <SidePanel setIsOpen={setIsOpen} title="פרטי שיעור">
        <div className={styles.container}>
          {/* ── Service badge ── */}
          <div className={styles.serviceBadge}>
            <span className={styles.serviceIcon}>🏋️</span>
            <span className={styles.serviceName}>{serviceName}</span>
          </div>

          {/* ── Info row ── */}
          <div className={styles.infoRow}>
            <div className={styles.infoChip}>
              <CalendarIcon1Svg fill="#DE1468" />
              <span>
                {dayName} {dateStr}
              </span>
            </div>
            <div className={styles.infoChip}>
              <ClockIcon1Svg fill="#DE1468" />
              <span>
                {appt.timeSlot} – {endTime}
              </span>
            </div>
            <div
              className={`${styles.capacityChip} ${
                count >= maxQueue
                  ? styles.chipRed
                  : count >= maxQueue * 0.8
                  ? styles.chipAmber
                  : styles.chipGreen
              }`}
            >
              {count}/{maxQueue}
            </div>
          </div>

          {/* ── Participants list ── */}
          <div className={styles.participantsSection}>
            <div className={styles.sectionTitle}>
              משתתפים
              <span className={styles.count}>{count}</span>
            </div>

            {loading ? (
              <div className={styles.emptyState}>טוען...</div>
            ) : members.length === 0 ? (
              <div className={styles.emptyState}>אין משתתפים רשומים</div>
            ) : (
              <div className={styles.participantsList}>
                {members.map((m) => (
                  <div key={m.userId} className={styles.participantRow}>
                    <div
                      className={styles.participantInfo}
                      onClick={() => {
                        if (onClientClick) {
                          onClientClick({
                            userId: m.userId,
                            customerName: m.userName,
                            phone: m.userPhone,
                          });
                        } else {
                          navigate(paths.clientMemberShip(m.userId));
                          setIsOpen(false);
                        }
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <GenerateFirstLetterBox name={m.userName} />
                      <span className={styles.participantName}>
                        {m.userName}
                      </span>
                      {getStatusBadge(m.hasAttended)}
                    </div>
                    <div className={styles.participantActions}>
                      <button
                        onClick={() => markAttendance(true, m.userId)}
                        title="אישור הגעה"
                        className={styles.iconBtn}
                      >
                        ✓
                      </button>
                      <button
                        onClick={() => markAttendance(false, m.userId)}
                        title="לא הגיע/ה"
                        className={`${styles.iconBtn} ${styles.iconBtnRed}`}
                      >
                        ✕
                      </button>
                      {m.userPhone && (
                        <button
                          onClick={() => redirectToWhatsapp(m.userPhone)}
                          title="WhatsApp"
                          className={styles.iconBtn}
                        >
                          <img
                            src="/assets/whatsapp-icon-1.svg"
                            alt="wa"
                            width={16}
                          />
                        </button>
                      )}
                      <button
                        onClick={() => handleCancelParticipant(m.userId)}
                        title="ביטול"
                        className={`${styles.iconBtn} ${styles.iconBtnRed}`}
                      >
                        <img
                          src="/assets/remove-icon-1.svg"
                          alt="cancel"
                          width={14}
                        />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Quick assign ── */}
          <div className={styles.assignSection}>
            {!showAssign ? (
              <button
                className={styles.assignToggleBtn}
                onClick={() => setShowAssign(true)}
              >
                + שבץ לקוח/ה
              </button>
            ) : (
              <div className={styles.assignPanel}>
                <input
                  type="text"
                  className={styles.assignSearch}
                  placeholder="חיפוש לפי שם או טלפון..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
                {searchLoading && (
                  <div className={styles.assignLoading}>מחפש...</div>
                )}
                {searchResults.length > 0 && (
                  <div className={styles.assignResults}>
                    {searchResults.map((c) => {
                      // API returns nested {user: {name, phone, _id}} or flat {name, phone, userId}
                      const user = c.user || c;
                      const cId = c.userId || user._id || c._id;
                      const cName = user.name || c.name || "";
                      const cPhone = user.phone || c.phone || "";
                      return (
                        <div key={cId} className={styles.assignResultRow}>
                          <div className={styles.assignResultInfo}>
                            <GenerateFirstLetterBox name={cName} />
                            <div>
                              <div className={styles.assignResultName}>
                                {cName}
                              </div>
                              <div className={styles.assignResultPhone}>
                                {cPhone}
                              </div>
                            </div>
                          </div>
                          <button
                            className={styles.assignBtn}
                            onClick={() =>
                              handleAssignClient({
                                userId: cId,
                                name: cName,
                                phone: cPhone,
                              })
                            }
                            disabled={assignLoading === cId}
                          >
                            {assignLoading === cId ? "..." : "שבץ"}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                {searchQuery.length >= 2 &&
                  !searchLoading &&
                  searchResults.length === 0 && (
                    <div className={styles.assignLoading}>לא נמצאו תוצאות</div>
                  )}
                <button
                  className={styles.assignCancelBtn}
                  onClick={() => {
                    setShowAssign(false);
                    setSearchQuery("");
                    setSearchResults([]);
                  }}
                >
                  ביטול
                </button>
              </div>
            )}
          </div>

          {/* ── Full view button ── */}
          <button className={styles.fullViewBtn} onClick={goToFullView}>
            עבור לתצוגה מלאה
          </button>
        </div>
      </SidePanel>

      {commentModalFor && (
        <CommentModal
          setIsOpen={() => setCommentModalFor(null)}
          appointmentId={appt._id}
        />
      )}
    </>
  );
};

export default ClassDetailsModal;
